import { useState, useCallback, useEffect } from "react";
import { computeDefaultWeightages, calcMaxWeightage } from "../utils/jdUtils";


// ── Helpers ────────────────────────────────────────────────────────────────────

// Maps old proficiency values (stored in DB) → new difficulty values
const PROFICIENCY_TO_DIFFICULTY = {
  Beginner: "easy",
  Intermediate: "medium",
  Advanced: "hard",
  Expert: "hard",
};

// Parse saved roundRequirements JSON → { topics, weightages, difficulties } maps.
function parseRoundRequirements(raw) {
  const out = { topics: {}, weightages: {}, difficulties: {} };
  if (!raw) return out;
  try {
    const reqs = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!Array.isArray(reqs)) return out;
    reqs.forEach((req) => {
      if (!req.skill) return;
      if (req.topics) out.topics[req.skill] = req.topics;
      if (req.weightage) out.weightages[req.skill] = req.weightage;
      // Support both new `difficulty` field and old `proficiency` field
      const raw_val = req.difficulty || req.proficiency;
      if (raw_val) out.difficulties[req.skill] = PROFICIENCY_TO_DIFFICULTY[raw_val] || raw_val;
    });
  } catch (_) {}
  return out;
}

// Parse focus areas string → trimmed, non-empty array (max 10 skills).
function parseFocusAreas(focusAreas) {
  return focusAreas.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 10);
}

// Check if two skill arrays have identical contents in order.
function sameSkillList(a, b) {
  return a.length === b.length && a.every((s, i) => s === b[i]);
}

// Build difficulties map, falling back to "medium" for any missing skill.
function initDifficulties(skills, savedDifficulties) {
  return skills.reduce(
    (acc, skill) => ({ ...acc, [skill]: savedDifficulties[skill] || "medium" }),
    {}
  );
}


// ── Hook ───────────────────────────────────────────────────────────────────────

export function useRequirementsState({
  round,
  roundConfigs,
  currentStep,
  roundConfigSubStep,
}) {

  // ── State ──────────────────────────────────────────────────────────────────

  const [requirementsState, setRequirementsState] = useState({
    skills: [],
    selectedTopics: {},   // { [skill]: string[] }
    customTopics: {},     // { [skill]: string[] }
    suggestions: {},      // { [skill]: Topic[] }
    loadingSkills: {},    // { [skill]: bool }
    weightages: {},       // { [skill]: number }
    difficulties: {},     // { [skill]: "easy" | "medium" | "hard" }
    completing: false,
    error: null,
    showCustomInput: false,
    customInput: "",
    topicsChanged: false,
  });


  // ── Updaters ───────────────────────────────────────────────────────────────

  const updateRequirementsState = useCallback((updates) => {
    setRequirementsState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Functional updater — required for concurrent skill fetches to avoid stale closure overwrites
  const updateRequirementsStateFn = useCallback((fn) => {
    setRequirementsState((prev) => ({ ...prev, ...fn(prev) }));
  }, []);


  // ── Weightage substep init ─────────────────────────────────────────────────
  // Populates skills + weightages + difficulties when reaching the Weightage screen.
  // Skips if already initialised for the same skill set.

  useEffect(() => {
    if (currentStep !== 2 || roundConfigSubStep !== "weightage") return;
    if (!round.id) return;

    const focusAreas = roundConfigs[round.id]?.focus || round.focus || "";
    if (!focusAreas) return;

    const skills = parseFocusAreas(focusAreas);

    setRequirementsState((prev) => {
      const alreadyInit =
        sameSkillList(prev.skills, skills) &&
        Object.keys(prev.weightages).length === skills.length;
      if (alreadyInit) return prev;

      const saved = parseRoundRequirements(round.roundRequirements);
      const savedSkills = Object.keys(saved.weightages);
      const weightagesMatch = skills.every(s => savedSkills.includes(s));
      return {
        ...prev,
        skills,
        weightages: computeDefaultWeightages(skills, weightagesMatch ? saved.weightages : {}),
        difficulties: initDifficulties(skills, saved.difficulties),
      };
    });
  }, [currentStep, roundConfigSubStep, round.id, round.roundRequirements, roundConfigs, round.focus]);


  // ── Requirements substep init ──────────────────────────────────────────────
  // Populates all fields (including topics) when entering Requirements screen.
  // Preserves existing suggestions cache to avoid re-fetching on back-navigation.

  useEffect(() => {
    if (currentStep !== 2 || roundConfigSubStep !== "requirements") return;
    if (!round.id) return;

    const focusAreas = roundConfigs[round.id]?.focus || round.focus || "";
    if (!focusAreas) return;

    const skills = parseFocusAreas(focusAreas);
    const saved = parseRoundRequirements(round.roundRequirements);

    const selectedTopics = skills.reduce(
      (acc, skill) => ({ ...acc, [skill]: saved.topics[skill] || [] }),
      {}
    );
    const customTopics = skills.reduce((acc, skill) => ({ ...acc, [skill]: [] }), {});

    setRequirementsState((prev) => {
      return {
        ...prev,
        // Reuse same array reference if skill contents are identical — prevents pre-fetch
        // effect in CustomStep4_Requirements from re-firing when this effect runs again.
        skills: sameSkillList(prev.skills, skills) ? prev.skills : skills,
        selectedTopics,
        customTopics,
        // Preserve live weightages + difficulties from Weightage substep if already set;
        // fall back to saved/default only on first entry
        weightages: Object.keys(prev.weightages).length > 0
          ? prev.weightages
          : computeDefaultWeightages(skills, saved.weightages),
        difficulties: Object.keys(prev.difficulties).length > 0
          ? prev.difficulties
          : initDifficulties(skills, saved.difficulties),
        // Preserve suggestions + loadingSkills — avoids re-fetching after back-navigation
        error: null,
        customInput: "",
        showCustomInput: false,
      };
    });
  }, [currentStep, roundConfigSubStep, round.id, round.roundRequirements, roundConfigs, round.focus]);


  // ── Topic handlers ─────────────────────────────────────────────────────────

  const handleTopicToggle = useCallback((skill, topic, maxTopics = 2) => {
    setRequirementsState((prev) => {
      const current = prev.selectedTopics[skill] || [];
      const isSelected = current.includes(topic);

      if (isSelected) {
        return {
          ...prev,
          topicsChanged: true,
          selectedTopics: { ...prev.selectedTopics, [skill]: current.filter((t) => t !== topic) },
        };
      }

      if (current.length >= maxTopics) return prev;

      return {
        ...prev,
        topicsChanged: true,
        selectedTopics: { ...prev.selectedTopics, [skill]: [...current, topic] },
      };
    });
  }, []);

  const handleCustomTopicAdd = useCallback((skill, topic, maxTopics = 2) => {
    setRequirementsState((prev) => {
      const current = prev.selectedTopics[skill] || [];

      if (current.includes(topic)) {
        return { ...prev, error: "This topic is already selected" };
      }
      if (current.length >= maxTopics) {
        return { ...prev, error: `Maximum ${maxTopics} topics allowed per skill` };
      }

      return {
        ...prev,
        selectedTopics: { ...prev.selectedTopics, [skill]: [...current, topic] },
        customTopics: {
          ...prev.customTopics,
          [skill]: [...(prev.customTopics[skill] || []), topic],
        },
        customInput: "",
        showCustomInput: false,
        error: null,
      };
    });
  }, []);


  // ── Weightage handlers ─────────────────────────────────────────────────────

  const handleWeightageChange = useCallback((changedSkill, newValue) => {
    setRequirementsState((prev) => {
      const allSkills = Object.keys(prev.weightages);
      const maxAllowed = calcMaxWeightage(allSkills.length);
      const numValue = Math.max(10, Math.min(maxAllowed, Math.round(Number(newValue))));
      return { ...prev, weightages: { ...prev.weightages, [changedSkill]: numValue } };
    });
  }, []);

  const handleDifficultyChange = useCallback((skill, value) => {
    setRequirementsState((prev) => ({
      ...prev,
      difficulties: { ...prev.difficulties, [skill]: value },
      topicsChanged: true,
    }));
  }, []);

  // Equal distribution — reuses computeDefaultWeightages with no existing values
  const handleAutoDistributeWeightage = useCallback(() => {
    setRequirementsState((prev) => {
      if (!prev.skills.length) return prev;
      return { ...prev, weightages: computeDefaultWeightages(prev.skills, {}) };
    });
  }, []);


  // ── Return ─────────────────────────────────────────────────────────────────

  return {
    requirementsState,
    updateRequirementsState,
    updateRequirementsStateFn,
    handleTopicToggle,
    handleCustomTopicAdd,
    handleWeightageChange,
    handleDifficultyChange,
    handleAutoDistributeWeightage,
  };
}
